import { useContext } from "react";
import { AuthContext, type AuthContextValue } from "./auth-context";

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth must be used within an AuthProvider.\n" +
        "Wrap your app with <AuthProvider> in your App component.",
    );
  }

  return context;
}

export function useUser() {
  const { user } = useAuth();
  return user;
}

export function useIsAuthenticated() {
  const { isAuthenticated, isLoading } = useAuth();
  return { isAuthenticated, isLoading };
}

export function useProfile() {
  const { profile, refreshProfile } = useAuth();
  return { profile, refreshProfile };
}

export function useHasRole(role: string) {
  const { profile } = useAuth();
  return profile?.role === role;
}

export function useHasAnyRole(roles: string[]) {
  const { profile } = useAuth();
  return profile?.role ? roles.includes(profile.role) : false;
}

export function useSession() {
  const { session } = useAuth();
  return session;
}

export function useAuthLoading() {
  const { isLoading } = useAuth();
  return isLoading;
}

export function useAuthError() {
  const { error } = useAuth();
  return error;
}
